import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { cashFlowData, cashFlowSettings } from '../data/cashflow';

const CashFlowReport: React.FC = () => {
  const [balance, setBalance] = useState<number>(0);
  const [moneyOut, setMoneyOut] = useState<number>(0);
  
  const updateCashFlow = () => {
    // This would normally update the cash flow data based on user inputs
    // For this demo, we'll just update the state variables
    setMoneyOut(moneyOut + 1000);
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Cash Flow Analysis</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Daily Cash Flow</h2>
          <div className="mt-4 md:mt-0 flex items-center">
            <div className="mr-4">
              <span className="block text-sm text-gray-600">Account:</span>
              <span className="font-semibold">{cashFlowSettings.account}</span>
            </div>
            <div className="mr-4">
              <span className="block text-sm text-gray-600">End Date:</span>
              <span className="font-semibold">{format(new Date(cashFlowSettings.endDate), 'MMM dd, yyyy')}</span>
            </div>
            <div>
              <span className="block text-sm text-gray-600">Game Over:</span>
              <span className="font-semibold">{cashFlowSettings.gameOver}</span>
            </div>
          </div>
        </div>
        
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={cashFlowData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => format(new Date(value), 'MMM dd')}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => format(new Date(value), 'MMMM dd, yyyy')}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="balance" 
                name="Balance" 
                stroke="#8884d8" 
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Cash Flow Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Starting Balance
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                  value={balance}
                  onChange={(e) => setBalance(Number(e.target.value))}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Money Out
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                  value={moneyOut}
                  onChange={(e) => setMoneyOut(Number(e.target.value))}
                  readOnly
                />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                id="includeDiscretionary"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={cashFlowSettings.includeDiscretionary}
                readOnly
              />
              <label htmlFor="includeDiscretionary" className="ml-2 block text-sm text-gray-900">
                Include Discretionary Expenses
              </label>
            </div>
            
            <button
              type="button"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={updateCashFlow}
            >
              Update Cash Flow
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Cash Flow Summary</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Current Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Today's Balance</p>
                  <p className="text-2xl font-bold">${balance.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Total Money Out</p>
                  <p className="text-2xl font-bold">${moneyOut.toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Forecast</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500">End of Period Balance</p>
                <p className="text-2xl font-bold">${(balance - moneyOut).toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {balance - moneyOut >= 0 
                    ? "Your cash flow is positive through the end of the period." 
                    : "Warning: Your cash flow will be negative by the end of the period."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Daily Cash Flow Details</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Day
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Balance
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {cashFlowData.map((day, index) => (
                <tr key={day.date} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-2 px-4 border-b border-gray-200">
                    {format(new Date(day.date), 'MMM dd, yyyy')}
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200">{day.day}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${day.balance.toLocaleString()}</td>
                  <td className="py-2 px-4 border-b border-gray-200">
                    {day.gameOver ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Game Over
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        OK
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CashFlowReport;